import React, { useEffect, useState } from 'react';
import { Typography, Row, Col, Card, Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCategories } from '../store/slices/categoriesSlice';
import { fetchProducts } from '../store/slices/productsSlice';
import ProductList from '../components/products/ProductList';
import ProductCarousel from '../components/products/ProductCarousel';
import Loader from '../components/common/Loader';
import productService from '../services/productService';
import { motion } from 'framer-motion';
import { Sparkles, ArrowRight, Activity, Percent } from 'lucide-react';

const { Title, Text } = Typography;

const HomePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { categories, loading: categoriesLoading } = useSelector((state) => state.categories);
  const { products, loading: productsLoading } = useSelector((state) => state.products);
  
  const [saleProducts, setSaleProducts] = useState([]);
  const [saleLoading, setSaleLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchCategories());
    dispatch(fetchProducts({ limit: 8 }));
    
    // Fetch products on sale
    const fetchSaleProducts = async () => {
      setSaleLoading(true);
      try {
        const response = await productService.getAll({ isOnSale: true, limit: 5 });
        setSaleProducts(response.products);
      } catch (error) {
        // error handling omitted for strict linting
      } finally {
        setSaleLoading(false);
      }
    };
    
    fetchSaleProducts();
  }, [dispatch]);

  const handleCategoryClick = (categoryId) => {
    navigate(`/products?category=${categoryId}`);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } }
  };

  if (categoriesLoading) {
    return <Loader text="Yuklanmoqda..." />;
  }

  const FloatingShapes = () => (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden' }}>
      <motion.div animate={{ y: [0, -20, 0], x: [0, 10, 0], rotate: [0, 10, 0] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }} style={{ position: 'absolute', top: '5%', left: '5%', width: 80, height: 80, background: 'rgba(255,255,255,0.15)', borderRadius: '50%', filter: 'blur(20px)' }} />
      <motion.div animate={{ y: [0, 20, 0], x: [0, -15, 0], rotate: [0, -15, 0] }} transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }} style={{ position: 'absolute', bottom: '10%', right: '5%', width: 120, height: 120, background: 'rgba(255,255,255,0.1)', borderRadius: '30%', filter: 'blur(30px)' }} />
    </div>
  );

  return (
    <motion.div initial="hidden" animate="visible" variants={containerVariants} style={{ width: '100%', padding: '0 20px 24px 20px', position: 'relative', overflowX: 'hidden' }}>
      <Row gutter={[32, 24]} style={{ marginTop: 16 }}>
        
        {/* Left Main Content */}
        <Col xs={24} lg={17} xl={19} xxl={20}>
          {/* Welcome Section */}
          <motion.div variants={itemVariants} style={{ position: 'relative', marginBottom: 28, textAlign: 'center', padding: '48px 16px', borderRadius: 24, color: 'white', overflow: 'hidden', boxShadow: '0 12px 32px rgba(14, 165, 233, 0.25)' }} className="bg-gradient-animate">
            <FloatingShapes />
            <div style={{ position: 'relative', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.2, duration: 0.4 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.25)', padding: '6px 14px', borderRadius: 20, marginBottom: 16, fontWeight: 600, fontSize: '0.85rem', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.2)' }}>
                  <Sparkles size={16} className="text-yellow-300" color="#fef08a" /> O&apos;zbekiston bo&apos;ylab
                </span>
              </motion.div>
              <Title level={1} style={{ color: 'white', margin: '0 0 12px 0', fontSize: 'clamp(2rem, 6vw, 3.5rem)', fontWeight: 800, letterSpacing: '-1px', lineHeight: 1.1, fontFamily: 'Outfit' }}>
                Megadent Shop&apos;ga <br />xush kelibsiz!
              </Title>
              <Text style={{ fontSize: '1rem', color: 'rgba(255, 255, 255, 0.95)', maxWidth: 650, display: 'inline-block', marginBottom: 24, fontWeight: 400, lineHeight: 1.5 }}>
                Eng yaxshi stomatologiya jihozlari endi bir qadam yoningizda. Katta chegirmalar va yuqori sifatli xizmatlardan bahramand bo&apos;ling.
              </Text>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button type="primary" onClick={() => navigate('/products')} style={{ height: 48, padding: '0 32px', borderRadius: 24, fontSize: '1.05rem', fontWeight: 600, background: 'white', color: '#0ea5e9', border: 'none', display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 8px 16px rgba(0,0,0,0.1)' }}>
                  Katalogga o&apos;tish <ArrowRight size={18} />
                </Button>
              </motion.div>
            </div>
          </motion.div>

          {/* Sale Carousel */}
          {!saleLoading && saleProducts && saleProducts.length > 0 && (
            <motion.div variants={itemVariants} style={{ marginBottom: 32 }}>
              <ProductCarousel products={saleProducts} />
            </motion.div>
          )}

          {/* Featured Products */}
          <motion.div variants={itemVariants} style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ padding: 12, background: '#fef3c7', borderRadius: 16, color: '#f59e0b', boxShadow: '0 4px 12px rgba(245, 158, 11, 0.15)' }}><Percent size={24} /></div>
                <Title level={3} style={{ margin: 0, fontSize: 28, fontWeight: 800, fontFamily: 'Outfit', color: '#f8fafc' }}>Ommabop Mahsulotlar</Title>
              </div>
              <Button type="primary" ghost shape="round" onClick={() => navigate('/products')} style={{ height: 36, padding: '0 20px', fontWeight: 600, fontSize: '0.9rem' }}>Barchasi</Button>
            </div>
            <ProductList products={products} loading={productsLoading} />
          </motion.div>
        </Col>

        {/* Right Sidebar - Categories */}
        <Col xs={24} lg={7} xl={5} xxl={4}>
          <motion.div variants={itemVariants} style={{ position: 'sticky', top: 24, paddingBottom: 65 /* To prevent covering by bottom bar visually if scrolled */ }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              <div style={{ padding: 12, background: '#e0f2fe', borderRadius: 16, color: '#0ea5e9', boxShadow: '0 4px 12px rgba(14, 165, 233, 0.15)' }}><Activity size={24} /></div>
              <Title level={3} style={{ margin: 0, fontSize: 24, fontWeight: 800, fontFamily: 'Outfit', color: '#f8fafc' }}>
                Kategoriyalar
              </Title>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {categories
                .filter((cat) => !cat.parent_id)
                .map((category) => (
                  <motion.div whileHover={{ x: 6, scale: 1.02 }} transition={{ type: "spring", stiffness: 300, damping: 20 }} key={category.id}>
                    <Card
                      hoverable
                      className="glass"
                      style={{ borderRadius: 20, border: '1px solid rgba(255, 255, 255, 0.1)', overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.2)' }}
                      bodyStyle={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16 }}
                      onClick={() => handleCategoryClick(category.id)}
                    >
                      <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg, #0ea5e9 0%, #8b5cf6 100%)', display: 'flex', minWidth: 48, alignItems: 'center', justifyContent: 'center', color: '#ffffff', fontSize: 22, fontWeight: '800', fontFamily: 'Outfit', boxShadow: '0 4px 12px rgba(139, 92, 246, 0.25)' }}>
                        {category.name.charAt(0)}
                      </div>
                      <div>
                        <Title level={5} style={{ margin: '0 0 6px 0', color: '#1e293b', fontWeight: 800, fontSize: '1.1rem', lineHeight: 1.2 }}>
                          {category.name}
                        </Title>
                        {category.children && category.children.length > 0 && (
                          <Text type="secondary" style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b', background: '#f8fafc', padding: '2px 8px', borderRadius: 12, border: '1px solid #e2e8f0' }}>
                            {category.children.length} ta mavjud
                          </Text>
                        )}
                      </div>
                    </Card>
                  </motion.div>
                ))}
            </div>

            {/* Promo Banner sidebar */}
            <motion.div whileHover={{ scale: 1.02 }} style={{ marginTop: 32, padding: '24px 20px', borderRadius: 24, background: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)', cursor: 'pointer', boxShadow: '0 12px 24px rgba(244, 114, 182, 0.15)', border: '1px solid rgba(255,255,255,0.5)' }} onClick={() => navigate('/products?sale=true')}>
               <div style={{ background: '#f9a8d4', width: 44, height: 44, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#be185d', marginBottom: 16 }}>
                 <Percent size={24} />
               </div>
               <Title level={4} style={{ color: '#9d174d', margin: '0 0 8px 0', fontWeight: 800, fontFamily: 'Outfit', lineHeight: 1.2 }}>Maxsus Chegirmalar!</Title>
               <Text style={{ display: 'block', color: '#be185d', fontWeight: 500, fontSize: '0.9rem', lineHeight: 1.5 }}>Faqat bugun super bonuslar va katta chegirmalardan foydalaning.</Text>
            </motion.div>
          </motion.div>
        </Col>
      </Row>
    </motion.div>
  );
};

export default HomePage;
